<template>
  <div class="position-relative">
    <b-card class="mt-4">
      <div class="d-flex justify-content-end mb-3">
        <b-button variant="info" size="sm" class="mr-2" :disabled="!categoriesWithVariableComponents.length"
          @click="openStructuresBillModal">
          <i class="fas fa-layer-group mr-1"></i> Generate Bill
        </b-button>
        <b-button variant="outline-info" size="sm" @click="showBillHistory">
          <i class="fas fa-history mr-1"></i> View Bill History
        </b-button>
      </div>

      <div v-if="!categoriesWithVariableComponents.length"
        class="coming-soon-box d-flex flex-column align-items-center justify-content-center text-center py-5">
        <i class="fas fa-info-circle fa-2x text-muted mb-2"></i>
        <span class="text-muted">No fee categories with components yet.</span>
      </div>

      <div v-else class="table-responsive rounded mt-1">
        <table class="table table-striped table-bordered">
          <thead class="thead-dark text-left">
            <tr>
              <th>#</th>
              <th>Fee Structure</th>
              <th>Component</th>
              <th>Billing Frequency</th>
              <th>Bills</th>
              <th>Generate Bill</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in variableComponentRows" :key="row.key">
              <td v-if="row.isFirstOfCategory" :rowspan="row.rowspan">{{ row.categoryIndex }}</td>
              <td v-if="row.isFirstOfCategory" :rowspan="row.rowspan">{{ row.categoryName }}</td>
              <td>{{ row.component.name }}</td>
              <td class="text-capitalize">{{ row.component.billing_frequency }}</td>
              <td>
                <b-link v-if="billsByComponent[row.component.id]" @click="showComponentBills(row)">
                  {{ billsByComponent[row.component.id].length }}
                  {{ billsByComponent[row.component.id].length === 1 ? 'bill' : 'bills' }}
                </b-link>
                <span v-else class="text-muted">0 bills</span>
              </td>
              <td>
                <b-button size="sm" variant="info" @click="generateBill(row)">Generate Bill</b-button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Generate Bill Modal -->
      <b-modal id="GenerateBillModal" hide-footer centered>
        <div class="p-3 border rounded">
          <h6 v-if="billModalRow">
            Generate Bill for: <strong>{{ billModalRow.component.name }}</strong>
            <small class="text-muted d-block">{{ billModalRow.categoryName }}</small>
          </h6>
          <b-form-group>
            <label>Bill Name</label><span class="text-danger">*</span>
            <b-form-input v-model.trim="billForm.name" placeholder="e.g. Bill Name" />
          </b-form-group>

          <b-form-group>
            <label>Start Date</label><span class="text-danger">*</span>
            <b-form-input v-model="billForm.start_date" type="date" />
          </b-form-group>

          <b-form-group>
            <label>End Date</label>
            <b-form-input v-model="billForm.end_date" type="date" />
          </b-form-group>

          <b-form-group>
            <label>Due Date (Pay with Fine)</label>
            <b-form-input v-model="billForm.due_date" type="date" />
          </b-form-group>

          <template v-if="billModalRow && billModalRow.component.billing_frequency === 'monthly'">
            <b-form-group label="Month">
              <b-form-select v-model="billForm.month" :options="monthOptions" class="form-control-sm" />
            </b-form-group>
          </template>

          <template v-else-if="billModalRow && billModalRow.component.billing_frequency === 'semester'">
            <b-form-group label="Academic Year">
              <b-form-select v-model="billForm.academicYear" :options="$acedemicYears" class="form-control-sm" />
            </b-form-group>
            <b-form-group label="Semester">
              <b-form-select v-model="billForm.semester" :options="semesterOptions" class="form-control-sm" />
            </b-form-group>
          </template>

          <template v-else-if="billModalRow && billModalRow.component.billing_frequency === 'yearly'">
            <b-form-group label="Academic Year">
              <b-form-select v-model="billForm.academicYear" :options="$acedemicYears" class="form-control-sm" />
            </b-form-group>
          </template>

          <b-button variant="info" class="mt-3" @click="confirmGenerateBill">Generate</b-button>
        </div>
      </b-modal>

      <!-- Generate Bill Modal: pick structures, then a component common to all of them -->
      <b-modal id="GenerateBillsModal" hide-footer centered size="lg" title="Generate Bill">
        <b-form-group label="Fee Structures" label-class="font-weight-bold">
          <div class="structure-pick p-2">
            <b-form-checkbox v-for="category in categoriesWithVariableComponents" :key="category.id" inline
              v-model="structuresForm.categoryIds" :value="category.id">
              {{ category.name }}
            </b-form-checkbox>
          </div>
        </b-form-group>

        <b-form-group label="Component" label-class="font-weight-bold"
          :description="structuresForm.categoryIds.length && !commonComponentOptions.length
            ? 'No component (with the same billing frequency) is in all the selected structures.' : ''">
          <b-form-select v-model="structuresForm.componentKey" :disabled="!commonComponentOptions.length">
            <b-form-select-option :value="null" disabled>
              {{ structuresForm.categoryIds.length ? 'Select a component' : 'Select fee structures first' }}
            </b-form-select-option>
            <b-form-select-option v-for="option in commonComponentOptions" :key="option.value" :value="option.value">
              {{ option.text }}
            </b-form-select-option>
          </b-form-select>
        </b-form-group>

        <b-form-group>
          <label class="font-weight-bold">Bill Name</label><span class="text-danger">*</span>
          <b-form-input v-model.trim="structuresForm.name" placeholder="e.g. Bill Name" />
        </b-form-group>
        <b-row>
          <b-col cols="12" md="4">
            <b-form-group>
              <label class="font-weight-bold">Start Date</label><span class="text-danger">*</span>
              <b-form-input v-model="structuresForm.start_date" type="date" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4">
            <b-form-group label="End Date" label-class="font-weight-bold">
              <b-form-input v-model="structuresForm.end_date" type="date" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4">
            <b-form-group label="Due Date (Pay with Fine)" label-class="font-weight-bold">
              <b-form-input v-model="structuresForm.due_date" type="date" />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row v-if="selectedCommonFrequency && selectedCommonFrequency !== 'once'">
          <b-col v-if="selectedCommonFrequency === 'monthly'" cols="12" md="4">
            <b-form-group label="Month" label-class="font-weight-bold">
              <b-form-select v-model="structuresForm.month" :options="monthOptions" />
            </b-form-group>
          </b-col>
          <b-col v-if="selectedCommonFrequency === 'semester' || selectedCommonFrequency === 'yearly'" cols="12" md="4">
            <b-form-group label="Academic Year" label-class="font-weight-bold">
              <b-form-select v-model="structuresForm.academicYear" :options="$acedemicYears" />
            </b-form-group>
          </b-col>
          <b-col v-if="selectedCommonFrequency === 'semester'" cols="12" md="4">
            <b-form-group label="Semester" label-class="font-weight-bold">
              <b-form-select v-model="structuresForm.semester" :options="semesterOptions" />
            </b-form-group>
          </b-col>
        </b-row>

        <b-button variant="info" class="mt-2" :disabled="structuresSaving || !structuresForm.componentKey"
          @click="confirmStructuresBill">
          Generate for {{ structuresForm.categoryIds.length }}
          {{ structuresForm.categoryIds.length === 1 ? 'Structure' : 'Structures' }}
        </b-button>
      </b-modal>

      <!-- Bill History Modal -->
      <b-modal id="BillHistoryModal" hide-footer centered size="lg"
        :title="historyRow ? `Bills for ${historyRow.component.name} (${historyRow.categoryName})` : 'Bill History'">
        <div v-if="!historyBills.length" class="text-muted font-italic text-center py-3">
          No bills generated yet.
        </div>
        <div v-else class="table-responsive rounded">
          <table class="table table-striped table-bordered">
            <thead class="thead-dark text-left">
              <tr>
                <th>#</th>
                <th>Bill Name</th>
                <th>Fee Structure</th>
                <th>Component</th>
                <th>Period</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Due Date</th>
                <th>Students Billed</th>
                <th>Generated On</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(bill, index) in historyBills" :key="bill.id">
                <td>{{ index + 1 }}</td>
                <td>{{ bill.name }}</td>
                <td>{{ bill.category_name }}</td>
                <td>{{ bill.component_name }}</td>
                <td>{{ billPeriodLabel(bill) }}</td>
                <td>{{ bill.start_date ? formatDate(bill.start_date) : '-' }}</td>
                <td>{{ bill.end_date ? formatDate(bill.end_date) : '-' }}</td>
                <td>{{ bill.due_date ? formatDate(bill.due_date) : '-' }}</td>
                <td>{{ bill.students_billed }}</td>
                <td>{{ formatDate(bill.created_at) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </b-modal>
    </b-card>
  </div>
</template>

<script>
export default {
  data() {
    return {
      feeCategories: [],
      monthOptions: [
        { value: 1, text: 'January' },
        { value: 2, text: 'February' },
        { value: 3, text: 'March' },
        { value: 4, text: 'April' },
        { value: 5, text: 'May' },
        { value: 6, text: 'June' },
        { value: 7, text: 'July' },
        { value: 8, text: 'August' },
        { value: 9, text: 'September' },
        { value: 10, text: 'October' },
        { value: 11, text: 'November' },
        { value: 12, text: 'December' }
      ],
      semesterOptions: [
        { value: 'odd', text: 'Odd' },
        { value: 'even', text: 'Even' }
      ],
      billModalRow: null,
      billForm: {
        name: '',
        month: null,
        academicYear: null,
        semester: null,
        start_date: null,
        end_date: null,
        due_date: null
      },
      billHistory: [],
      // Row whose bills the history modal is limited to; null shows every bill
      historyRow: null,
      // "Generate Bill" modal - one bill per selected structure, for a
      // component they all share (componentKey = catalog id + frequency)
      structuresForm: {
        categoryIds: [],
        componentKey: null,
        name: '',
        start_date: null,
        end_date: null,
        due_date: null,
        month: null,
        academicYear: null,
        semester: null
      },
      structuresSaving: false,
    };
  },
  computed: {
    categoriesWithVariableComponents() {
      return this.feeCategories.filter((category) => this.variableComponents(category).length);
    },
    variableComponentRows() {
      const rows = [];
      this.categoriesWithVariableComponents.forEach((category, categoryIndex) => {
        const components = this.variableComponents(category);
        components.forEach((component, componentIndex) => {
          rows.push({
            key: `${category.id}-${component.id}`,
            categoryIndex: categoryIndex + 1,
            categoryName: category.name,
            isFirstOfCategory: componentIndex === 0,
            rowspan: components.length,
            component
          });
        });
      });
      return rows;
    },
    // Generated bills grouped by the component they were raised on, oldest first
    billsByComponent() {
      const grouped = {};
      [...this.billHistory].reverse().forEach((bill) => {
        (grouped[bill.fee_component_id] = grouped[bill.fee_component_id] || []).push(bill);
      });
      return grouped;
    },
    // Components present in every selected structure. Matched on the shared
    // catalog component AND billing frequency - one bill period has to fit
    // all of them, so "Transport (Monthly)" and "Transport (Yearly)" differ.
    commonComponentOptions() {
      const selected = this.feeCategories.filter((c) => this.structuresForm.categoryIds.includes(c.id));
      if (!selected.length) return [];

      const [first, ...rest] = selected;
      return first.components
        .filter((component) => rest.every((category) =>
          category.components.some((c) => this.commonComponentKey(c) === this.commonComponentKey(component))))
        .map((component) => ({
          value: this.commonComponentKey(component),
          text: `${component.name} (${this.frequencyLabel(component.billing_frequency)})`
        }));
    },
    selectedCommonFrequency() {
      if (!this.structuresForm.componentKey) return null;
      return this.structuresForm.componentKey.split('|')[1];
    },
    historyBills() {
      if (!this.historyRow) return this.billHistory;
      return this.billHistory.filter((bill) => bill.fee_component_id === this.historyRow.component.id);
    }
  },
  watch: {
    // Drop the picked component once it's no longer common to the selection.
    commonComponentOptions(options) {
      if (!options.some((option) => option.value === this.structuresForm.componentKey)) {
        this.structuresForm.componentKey = null;
      }
    }
  },
  async created() {
    await Promise.all([this.loadFeeCategories(), this.loadBillHistory()]);
  },
  methods: {
    async loadFeeCategories() {
      const url = this.$store.getters.getAPIKey.mainAPI + `/feecategories`;
      const response = await this.$axios.get(url);
      this.feeCategories = response.data;
    },
    variableComponents(category) {
      return category.components;
    },
    generateBill(row) {
      this.billModalRow = row;
      this.billForm = { name: '', month: null, academicYear: null, semester: null, start_date: null, end_date: null, due_date: null };
      this.$bvModal.show('GenerateBillModal');
    },
    async confirmGenerateBill() {
      if (!this.billForm.name) {
        this.$toast.error("Bill name is required");
        return;
      }
      if (!this.billForm.start_date) {
        this.$toast.error("Start date is required");
        return;
      }

      const frequency = this.billModalRow.component.billing_frequency;
      if (frequency === 'monthly' && !this.billForm.month) {
        this.$toast.error("Month is required");
        return;
      }
      if (frequency === 'semester' && (!this.billForm.academicYear || !this.billForm.semester)) {
        this.$toast.error("Academic Year and Semester are required");
        return;
      }
      if (frequency === 'yearly' && !this.billForm.academicYear) {
        this.$toast.error("Academic Year is required");
        return;
      }

      const url = this.$store.getters.getAPIKey.mainAPI + `/feebills`;
      const payload = {
        name: this.billForm.name,
        fee_component_id: this.billModalRow.component.id,
        month: frequency === 'monthly' ? this.billForm.month : null,
        academic_year: frequency === 'semester' || frequency === 'yearly' ? this.billForm.academicYear : null,
        semester: frequency === 'semester' ? this.billForm.semester : null,
        start_date: this.billForm.start_date,
        end_date: this.billForm.end_date,
        due_date: this.billForm.due_date
      };

      try {
        const response = await this.$axios.post(url, payload);
        this.$toast.success(response.data.message);
        this.$bvModal.hide('GenerateBillModal');
        this.loadBillHistory();
      } catch (error) {
        this.$toast.error(error.response?.data?.message || "Failed to generate bill");
      }
    },
    commonComponentKey(component) {
      return `${component.fee_component_id}|${component.billing_frequency}`;
    },
    frequencyLabel(frequency) {
      return frequency.charAt(0).toUpperCase() + frequency.slice(1);
    },
    openStructuresBillModal() {
      this.structuresForm = {
        categoryIds: [],
        componentKey: null,
        name: '',
        start_date: null,
        end_date: null,
        due_date: null,
        month: null,
        academicYear: null,
        semester: null
      };
      this.$bvModal.show('GenerateBillsModal');
    },
    // One bill per selected structure, each on that structure's own row for
    // the chosen component. A structure whose bill goes through is unticked,
    // so after a failure (e.g. already billed for that period) the user can
    // fix it and retry without generating the earlier ones twice.
    async confirmStructuresBill() {
      const form = this.structuresForm;
      const frequency = this.selectedCommonFrequency;

      if (!form.categoryIds.length || !form.componentKey) {
        this.$toast.error("Select fee structures and a component");
        return;
      }
      if (!form.name) {
        this.$toast.error("Bill name is required");
        return;
      }
      if (!form.start_date) {
        this.$toast.error("Start date is required");
        return;
      }
      if (frequency === 'monthly' && !form.month) {
        this.$toast.error("Month is required");
        return;
      }
      if (frequency === 'semester' && (!form.academicYear || !form.semester)) {
        this.$toast.error("Academic Year and Semester are required");
        return;
      }
      if (frequency === 'yearly' && !form.academicYear) {
        this.$toast.error("Academic Year is required");
        return;
      }

      const url = this.$store.getters.getAPIKey.mainAPI + `/feebills`;
      this.structuresSaving = true;
      try {
        while (form.categoryIds.length) {
          const category = this.feeCategories.find((c) => c.id === form.categoryIds[0]);
          const component = category.components.find((c) => this.commonComponentKey(c) === form.componentKey);
          try {
            await this.$axios.post(url, {
              name: form.name,
              fee_component_id: component.id,
              month: frequency === 'monthly' ? form.month : null,
              academic_year: frequency === 'semester' || frequency === 'yearly' ? form.academicYear : null,
              semester: frequency === 'semester' ? form.semester : null,
              start_date: form.start_date,
              end_date: form.end_date,
              due_date: form.due_date
            });
          } catch (error) {
            this.$toast.error(`${category.name}: ` + (error.response?.data?.message || "Failed to generate bill"));
            return;
          }
          form.categoryIds.shift();
        }
      } finally {
        this.structuresSaving = false;
        this.loadBillHistory();
      }

      this.$toast.success("Bills generated");
      this.$bvModal.hide('GenerateBillsModal');
    },
    async showBillHistory() {
      this.historyRow = null;
      await this.loadBillHistory();
      this.$bvModal.show('BillHistoryModal');
    },
    showComponentBills(row) {
      this.historyRow = row;
      this.$bvModal.show('BillHistoryModal');
    },
    async loadBillHistory() {
      const url = this.$store.getters.getAPIKey.mainAPI + `/feebills`;
      const response = await this.$axios.get(url);
      this.billHistory = response.data;
    },
    billPeriodLabel(bill) {
      if (bill.billing_frequency === 'monthly') {
        const fromText = this.monthOptions.find((m) => m.value === bill.from_month)?.text;
        const toText = this.monthOptions.find((m) => m.value === bill.to_month)?.text;
        return fromText === toText ? fromText : `${fromText} - ${toText}`;
      }
      if (bill.billing_frequency === 'semester') {
        const semesterText = this.semesterOptions.find((s) => s.value === bill.semester)?.text;
        return `${bill.academic_year} (${semesterText})`;
      }
      if (bill.billing_frequency === 'yearly') {
        return bill.academic_year;
      }
      return '-';
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
    },
  }
};
</script>

<style scoped>
.structure-pick {
  border: 1px dashed #0d6efd;
  border-radius: 6px;
}

.coming-soon-box {
  border: 1px dashed #dee2e6;
  border-radius: 12px;
  background: #f9f9fb;
}
</style>
